/**
 * Admin collaborators list component for workspace detail page.
 * Adds an existing user directly, without sending an invitation.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useAdminWorkspaceCollaborators,
  useAdminAddCollaboratorDirect,
} from "../../hooks/useAdminWorkspaces";
import {
  AlertCircle,
  Loader2,
  Users,
  UserPlus,
  Mail,
  Phone,
} from "lucide-react";
import { adminGetAllUsers, type AdminUser, type WorkspaceCollaborator, type WorkspaceCollaboratorRole } from "../../services/naraDoor";
import { WorkspaceRoleBadge } from "../../components/WorkspaceRoleBadge";
import { canAddOwner } from "../../lib/collaborators";

interface AdminCollaboratorsListProps {
  workspaceId: string;
}

function getStatusBadge(status?: string) {
  if (status === "invited") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300">
        Invited
      </span>
    );
  }
  return null;
}

export function AdminCollaboratorsList({ workspaceId }: AdminCollaboratorsListProps) {
  const navigate = useNavigate();
  const [showAddDialog, setShowAddDialog] = useState(false);

  const { data, isLoading, error } = useAdminWorkspaceCollaborators(workspaceId);

  const handleCollaboratorClick = (collaborator: WorkspaceCollaborator) => {
    navigate(`/admin/workspaces/${workspaceId}/collaborators/${collaborator.user_id}`);
  };
  const addMutation = useAdminAddCollaboratorDirect();
  const [userSearch, setUserSearch] = useState("");
  const [userResults, setUserResults] = useState<AdminUser[] | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [role, setRole] = useState("viewer");

  const collaborators = data?.collaborators || [];

  const closeAddDialog = () => {
    setShowAddDialog(false);
    setUserSearch("");
    setUserResults(null);
    setSearchError(null);
    setSelectedUser(null);
    setRole("viewer");
    addMutation.reset();
  };

  const handleSearchUsers = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userSearch.trim()) return;
    setSearchError(null);
    try {
      setUserResults((await adminGetAllUsers(0, 10, userSearch.trim())).users);
    } catch (err) {
      console.error("Failed to search users:", err instanceof Error ? err.message : err);
      setSearchError("Search failed. Try again.");
    }
  };

  const handleAddCollaborator = async () => {
    if (!selectedUser) return;
    try {
      await addMutation.mutateAsync({ workspaceId, userId: selectedUser.id, role });
      closeAddDialog();
    } catch (err) {
      console.error("Failed to add workspace collaborator:", err instanceof Error ? err.message : err);
    }
  };

  const userLabel = (u: AdminUser) =>
    [u.first_name, u.last_name].filter(Boolean).join(" ") || u.email || u.phone || u.id;

  const getCollaboratorName = (collaborator: WorkspaceCollaborator) => {
    if (collaborator.user?.first_name || collaborator.user?.last_name) {
      return `${collaborator.user.first_name || ""} ${collaborator.user.last_name || ""}`.trim();
    }
    return collaborator.user?.email || "Unknown";
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-14 bg-muted rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
        <AlertCircle className="h-5 w-5 text-red-600 shrink-0" />
        <p className="text-sm text-red-800 dark:text-red-200">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {collaborators.length} collaborator{collaborators.length !== 1 ? "s" : ""}
        </p>
        <button
          onClick={() => setShowAddDialog(true)}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <UserPlus className="h-4 w-4" />
          Add Collaborator
        </button>
      </div>

      {collaborators.length === 0 ? (
        <div className="text-center py-8 bg-muted rounded-lg border border-border">
          <Users className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No collaborators in this workspace</p>
        </div>
      ) : (
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted border-b border-border">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-foreground">
                  Name
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-foreground hidden sm:table-cell">
                  Contact
                </th>
                <th className="text-center px-4 py-3 text-sm font-medium text-foreground">
                  Role
                </th>
                <th className="text-center px-4 py-3 text-sm font-medium text-foreground hidden sm:table-cell">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {collaborators.map((collaborator) => (
                <tr
                  key={collaborator.id}
                  className="hover:bg-accent cursor-pointer"
                  onClick={() => handleCollaboratorClick(collaborator)}
                >
                  <td className="px-4 py-3">
                    <span className="font-medium text-foreground">
                      {getCollaboratorName(collaborator)}
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <div className="flex flex-col">
                      <span className="flex items-center gap-1 text-sm text-foreground">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        {collaborator.user?.email || "—"}
                      </span>
                      <span className="flex items-center gap-1 text-sm text-muted-foreground mt-0.5">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        {collaborator.user?.phone || "—"}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <WorkspaceRoleBadge role={collaborator.role as WorkspaceCollaboratorRole} />
                  </td>
                  <td className="px-4 py-3 text-center hidden sm:table-cell">
                    {getStatusBadge(collaborator.status)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Collaborator Dialog */}
      {showAddDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-sm mx-4">
            <div className="p-4 space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-foreground mb-1">Add Collaborator</h2>
                <p className="text-sm text-muted-foreground">
                  Add an existing user directly, without sending an invitation.
                </p>
              </div>
              {(searchError || addMutation.error) && (
                <div className="p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600">
                  {searchError || addMutation.error?.message}
                </div>
              )}
              {selectedUser ? (
                <div className="flex items-center justify-between gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-foreground">
                  <span className="truncate">{userLabel(selectedUser)}</span>
                  <button onClick={() => setSelectedUser(null)} className="text-muted-foreground hover:text-foreground">
                    Change
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSearchUsers} className="space-y-2">
                  <input
                    type="text"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Search users"
                    className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {userResults && (
                    <div className="max-h-48 overflow-y-auto border border-border rounded-lg divide-y divide-border">
                      {userResults.length ? userResults.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => setSelectedUser(u)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-accent"
                        >
                          {userLabel(u)}
                        </button>
                      )) : (
                        <p className="px-3 py-2 text-sm text-muted-foreground">No matching users</p>
                      )}
                    </div>
                  )}
                </form>
              )}
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {canAddOwner(collaborators) && <option value="owner">Owner</option>}
                <option value="editor">Editor</option>
                <option value="admin">Admin</option>
                <option value="viewer">Viewer</option>
              </select>
              <div className="flex justify-end gap-3">
                <button
                  onClick={closeAddDialog}
                  disabled={addMutation.isPending}
                  className="px-4 py-2 text-foreground hover:bg-accent rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddCollaborator}
                  disabled={!selectedUser || addMutation.isPending}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {addMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Add Collaborator
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
